import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Video, VideoOff, Mic, MicOff, Volume2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InterviewSession() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timer, setTimer] = useState(0);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [isQuestionStarted, setIsQuestionStarted] = useState(false);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const speechSynthRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  useEffect(() => {
    fetchInterviewData();
    initializeMedia();
    return () => {
      stopMedia();
      stopSpeech();
    };
  }, []);

  useEffect(() => {
    // Re-assign stream to video element when it changes
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isQuestionStarted]);

  useEffect(() => {
    if (isQuestionStarted && questions.length > 0) {
      speakQuestion(questions[currentQuestionIndex].question_text);
      startCountdown();
    }
  }, [isQuestionStarted, currentQuestionIndex]);

  const fetchInterviewData = async () => {
    try {
      const [interviewRes, questionsRes] = await Promise.all([
        axios.get(`${API}/interviews/${interviewId}`),
        axios.get(`${API}/questions`),
      ]);
      setInterview(interviewRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error("Error fetching interview data:", error);
    }
  };

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
    } catch (error) {
      console.error("Error accessing media:", error);
      alert("Mohon izinkan akses kamera dan mikrofon");
    }
  };

  const stopMedia = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const speakQuestion = (text) => {
    stopSpeech();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'id-ID';
    utterance.rate = 0.85; // Lebih lambat untuk kejelasan
    utterance.pitch = 1.0;
    utterance.volume = 1.0; // Volume maksimal
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeech = () => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
  };

  const toggleCamera = () => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      videoTrack.enabled = !cameraEnabled;
      setCameraEnabled(!cameraEnabled);
    }
  };

  const toggleMic = () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      audioTrack.enabled = !micEnabled;
      setMicEnabled(!micEnabled);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    
    recordedChunksRef.current = [];
    
    const options = {
      mimeType: 'video/webm;codecs=vp8,opus',
      videoBitsPerSecond: 2_000_000, // Lower bitrate for smaller file
    };
    
    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(); // Collect data every 1 second
      console.log("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(null);
        return;
      }
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log("Recording stopped, blob size:", blob.size);
        resolve(blob);
      };
      
      mediaRecorderRef.current.stop();
    });
  };

  // const blobToBase64 = (blob) => {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onloadend = () => resolve(reader.result.split(',')[1]);
  //     reader.onerror = reject;
  //     reader.readAsDataURL(blob);
  //   });
  // };

  const startCountdown = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const timeLimit = currentQuestion.time_limit || 60;
    setTimer(timeLimit);
    
    // Start recording
    startRecording();
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          handleAutoAdvance();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleAutoAdvance = async () => {
    await submitAnswer();
  };

  const captureFrame = () => {
    if (!videoRef.current) return "";
    
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    
    return canvas.toDataURL("image/jpeg", 0.8).split(",")[1];
  };

  // NEW HELPER FUNCTION: Mengubah Base64 Data URI (dari captureFrame) menjadi Blob
  const dataURItoBlob = (dataURI) => {
      // Memisahkan mime type dari base64 data
      const splitDataURI = dataURI.split(',');
      // Jika format Data URI benar, ambil mime type dan data Base64
      const mimeType = splitDataURI[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const base64Data = splitDataURI.length > 1 ? splitDataURI[1] : splitDataURI[0];

      // Decode Base64
      const byteString = atob(base64Data);
      
      // Buat ArrayBuffer
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      // Setel byte dari ArrayBuffer
      for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
      }
      
      // Buat Blob
      return new Blob([ab], { type: mimeType });
  };

  const submitAnswer = async () => {
    if (analyzing) return;
    
    setAnalyzing(true);
    stopSpeech();
    
    const currentQuestion = questions[currentQuestionIndex];
    // const frameImage = captureFrame();
    const frameImageBase64 = captureFrame();
    const answerText = "Jawaban direkam melalui video dan audio";

    try {
      // 2. Stop recording dan dapatkan video blob
      const videoBlob = await stopRecording();

      // Mengubah Base64 frameImage menjadi Blob/File untuk backend UploadFile
      let frameImageBlob = null;
      if (frameImageBase64) {
          frameImageBlob = dataURItoBlob('data:image/jpeg;base64,' + frameImageBase64); 
      }
      
      const formData = new FormData();
      formData.append("interview_id", interviewId);
      formData.append("question_id", currentQuestion.id);
      formData.append("question_text", currentQuestion.question_text);
      formData.append("transcript", answerText);
      
      // 3. PENGIRIMAN DATA BESAR SEBAGAI FILE/BLOB
      // Asumsi: Backend telah diubah untuk menerima:
      //   - frame_image_file (UploadFile)
      //   - video_recording_file (UploadFile)
      
      // Mengubah Base64 frameImage (kecil) menjadi Blob/File untuk backend UploadFile
      // const frameImageBlob = (frameImageBase64.length > 0) 
      //   ? dataURItoBlob('data:image/jpeg;base64,' + frameImageBase64) 
      //   : null;

      // Menggunakan nama field yang sama dengan perubahan di backend (Lihat Catatan 1)
      if (frameImageBlob) {
        formData.append("frame_image_file", frameImageBlob, 'frame.jpeg'); 
      }
      
      if (videoBlob) {
        formData.append("video_recording_file", videoBlob, 'recording.webm');
      }
      // Stop recording and get video blob
      // const videoBlob = await stopRecording();
      // let videoBase64 = "";
      
      // if (videoBlob) {
      //   videoBase64 = await blobToBase64(videoBlob);
      //   console.log("Video recording size:", videoBase64.length, "bytes");
      // }
      
      // const formData = new FormData();
      // formData.append("interview_id", interviewId);
      // formData.append("question_id", currentQuestion.id);
      // formData.append("question_text", currentQuestion.question_text);
      // formData.append("transcript", answerText);
      // formData.append("frame_image", frameImage);
      // formData.append("video_recording", videoBase64);
  
      // TAMBAHKAN LOGGING INI
      console.log("=== Submitting Answer ===");
      console.log("Interview ID:", interviewId);
      console.log("Question ID:", currentQuestion.id);
      console.log("Question Text:", currentQuestion.question_text);
      // console.log("Transcript:", answerText);
      // console.log("Frame Image Length:", frameImageBase64.length);
      console.log("Video Blob Size:", videoBlob ? videoBlob.size : 0, "bytes");
      console.log("Frame Image Blob Size:", frameImageBlob ? frameImageBlob.size : 0, "bytes");
  
      await axios.post(`${API}/analyze-answer`, formData);

      // Move to next question or complete
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setAnalyzing(false);
      } else {
        // Complete interview
        await completeInterview();
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
      alert("Gagal mengirim jawaban. Silakan coba lagi.");
      setAnalyzing(false);
    }
  };

  const completeInterview = async () => {
    try {
      await axios.patch(`${API}/interviews/${interviewId}/complete`);
      await axios.post(`${API}/interviews/${interviewId}/report`);
      stopMedia();
      navigate(`/results/${interviewId}`);
    } catch (error) {
      console.error("Error completing interview:", error);
    }
  };

  const startInterview = () => {
    setIsQuestionStarted(true);
    // Ensure video is playing
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(err => console.log("Video play error:", err));
    }
  };

  if (!interview || questions.length === 0) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  if (!isQuestionStarted) {
    return (
      <div className="interview-container" data-testid="interview-session">
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          padding: "2rem"
        }}>
          <div style={{ 
            fontSize: "1rem", 
            fontWeight: "700", 
            color: "#0891b2", 
            marginBottom: "1rem",
            letterSpacing: "2px"
          }}>
            RASPRO UNIVERSE
          </div>
          <h1 style={{ fontSize: "2.5rem", marginBottom: "1rem", color: "#0891b2" }}>
            Selamat Datang, {interview.candidate_name}!
          </h1>
          <p style={{ fontSize: "1.2rem", color: "#64748b", marginBottom: "2rem", maxWidth: "600px" }}>
            Anda akan menjawab {questions.length} pertanyaan. Setiap pertanyaan akan otomatis berpindah sesuai waktu dari masing-masing pertanyaan.
            Pastikan wajah Anda terlihat jelas pada video dan pastikan juga mikrofon Anda aktif. Tekan tombol di bawah untuk memulai interview.
          </p>
          <div className="video-container" style={{ maxWidth: "600px", marginBottom: "2rem" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="video-preview"
              data-testid="candidate-video"
              style={{ display: "block" }}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={startInterview}
            style={{ padding: "1.5rem 3rem", fontSize: "1.2rem" }}
            data-testid="start-interview-btn"
          >
            Mulai Interview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-container" data-testid="interview-session">
      <div className="interview-header">
        <div>
          <div style={{ 
            fontSize: "0.8rem", 
            fontWeight: "700", 
            color: "#0891b2", 
            marginBottom: "0.25rem",
            letterSpacing: "1.5px"
          }}>
            RASPRO UNIVERSE
          </div>
          <h2 style={{ fontSize: "1.3rem", color: "#0891b2", marginBottom: "0.25rem" }}>
            {interview.candidate_name}
          </h2>
          <p style={{ color: "#64748b", fontSize: "0.9rem" }}>{interview.position}</p>
        </div>
        <div style={{ textAlign: "center" }}>
          <div 
            className="timer" 
            data-testid="recording-timer"
            style={{ 
              fontSize: "3rem", 
              fontWeight: "700",
              color: timer <= 3 ? "#ef4444" : "#0891b2",
              marginBottom: "0.5rem"
            }}
          >
            {timer}
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
            detik tersisa
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.9rem", color: "#64748b", marginBottom: "0.25rem" }}>
            Progres
          </div>
          <div style={{ fontSize: "1.2rem", fontWeight: "600", color: "#0891b2" }}>
            {currentQuestionIndex + 1} / {questions.length}
          </div>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: "8px",
          background: "#e2e8f0",
          borderRadius: "10px",
          marginBottom: "2rem",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "linear-gradient(90deg, #0891b2 0%, #4fd1c5 100%)",
            transition: "width 0.3s ease",
          }}
          data-testid="progress-bar"
        />
      </div>

      <div className="video-section">
        <div className="video-container">
          <h3 style={{ marginBottom: "1rem", color: "#0891b2" }}>Video Anda</h3>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="video-preview"
            data-testid="candidate-video"
            style={{ display: "block" }}
          />
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button
              className={`btn ${cameraEnabled ? "btn-secondary" : "btn-primary"}`}
              onClick={toggleCamera}
              style={{ flex: 1 }}
              data-testid="toggle-camera-btn"
            >
              {cameraEnabled ? <Video size={20} /> : <VideoOff size={20} />}
              {cameraEnabled ? "Kamera Nyala" : "Kamera Mati"}
            </button>
            <button
              className={`btn ${micEnabled ? "btn-secondary" : "btn-primary"}`}
              onClick={toggleMic}
              style={{ flex: 1 }}
              data-testid="toggle-mic-btn"
            >
              {micEnabled ? <Mic size={20} /> : <MicOff size={20} />}
              {micEnabled ? "Mic Nyala" : "Mic Mati"}
            </button>
          </div>
        </div>

        <div className="question-card">
          <div className="question-number">
            <Volume2 size={20} style={{ verticalAlign: "middle", marginRight: "0.5rem" }} />
            Pertanyaan {currentQuestionIndex + 1} dari {questions.length}
          </div>
          <div className="question-text" data-testid="current-question">
            {currentQuestion.question_text}
          </div>
          <div style={{ marginTop: "2rem", fontSize: "0.9rem", opacity: 0.9 }}>
            Kategori: {currentQuestion.category} • Waktu: {currentQuestion.time_limit} detik
          </div>
        </div>
      </div>

      {analyzing && (
        <div className="video-container" style={{ marginBottom: "2rem" }}>
          <div style={{ 
            padding: "2rem", 
            background: "#e0f2fe", 
            borderRadius: "16px",
            textAlign: "center",
            color: "#0891b2",
            fontWeight: "600",
            fontSize: "1.2rem"
          }}>
            🤖 Menganalisis jawaban Anda dengan AI Gemini...
          </div>
        </div>
      )}
    </div>
  );
}
